import type { QuestionInsight } from '../components/InsightsPanel';

import { getLocalizedText, type LocalizedText } from '@/utils/localization';

/**
 * Reduces a section to the one string the panel renders. Current rows hold a plain string (the backend
 * generates in the caller's app language); rows written before that change hold `{ en, fr }`, and those
 * are collapsed with getLocalizedText so an existing insight still reads in the viewer's language.
 */
export function toInsightText(value: unknown): string {
    if (!value) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    return getLocalizedText(value as LocalizedText);
}

/**
 * The gateway delivers the insight as a JSON string. Each section is a plain string in the caller's
 * app language; rows generated before the single-language change still hold the bilingual
 * `{ en, fr }` shape, so both shapes are accepted and reduced to that one string.
 */
export function parseInsight(content: string | null): QuestionInsight {
    if (!content) {
        return { engagement: '', peer360: '', self: '' };
    }

    try {
        const parsed = JSON.parse(content) as Record<string, unknown>;
        return {
            engagement: toInsightText(parsed.engagement),
            peer360: toInsightText(parsed.peer360),
            self: toInsightText(parsed.self),
        };
    } catch {
        // Not JSON — surface the raw text rather than dropping it.
        return { engagement: content, peer360: '', self: '' };
    }
}

/** Returns a copy of the record without the specified key. */
export function omitKey<T>(record: Record<string, T>, key: string): Record<string, T> {
    if (!(key in record)) {
        return record;
    }
    const next = { ...record };
    delete next[key];
    return next;
}
